import styled from '@emotion/styled';
import {
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react-dom-interactions';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import type { MouseEventHandler } from 'react';
import {
  useMemo,
  useCallback,
  useState,
} from 'react';
import { lnbActiveAtom } from '.';
import {
  GnbDropDownItemDiv,
  GnbDropDownSpan,
} from '../GnbDropDown';
import type { GnbItem } from '../gnb-menu';
import NavLink from './NavLink';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { testIds } from '@/constants/test-ids';
import { useBreakpoint } from '@/lib/breakpoint.service';

type NavItemProps = {
  menu: GnbItem,
  isLastItem?: boolean,
};

const HideDropDownDiv = styled.div`
position: absolute;
top: 0;
`;

const Container = styled.div<{
  active: boolean,
  isLastItem?: boolean,
}>`
display: grid;
padding: 0 16px;
margin: 8px 0;
margin-right: ${({ isLastItem }) => isLastItem ? '-16px' : 0};
color: ${colors.black};
cursor: pointer;
font-size: 16px;
font-weight: 500;
line-height: 150%;
place-items: center;

@media (hover: hover) {
  &:hover {
    color: ${colors.blue}
  }
}

@media (max-width: ${breakpointMedium}) {
  color: ${colors.brightGrey};
  ${({ active }) => active && `
  color: ${colors.blue};
  `}
}
@media (max-width: ${breakpointSmall}) {
  font-size: 14px;
}
`;

const NavItem = ({ menu, isLastItem = false }: NavItemProps) => {
  const [lnbActive, setLnbActive] = useAtom(lnbActiveAtom);
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const breakpoint = useBreakpoint();

  const navItemActive = useMemo(
    () => lnbActive === menu || menu.children?.some(({ path }) => path === router.pathname) || false,
    [lnbActive, menu, router],
  );

  const isNarrow = useMemo(
    () => ['sm', 'md'].includes(breakpoint),
    [breakpoint],
  );

  const {
    context,
    floating,
    reference,
    strategy,
    x,
    y,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [shift({ padding: 8 })],
  });

  const {
    getFloatingProps,
    getReferenceProps,
  } = useInteractions([
    useClick(context, {
      ignoreMouse: true,
    }),
    useDismiss(context),
    useHover(context, {
      handleClose: isNarrow ? null : safePolygon(),
      mouseOnly: true,
    }),
  ]);

  const referenceProps = getReferenceProps({ ref: reference });
  const floatingProps = getFloatingProps({ ref: floating });

  const handleClick: MouseEventHandler = useCallback(() => {
    isNarrow && setLnbActive(
      lnbActive === menu ? null : menu,
    );
  }, [isNarrow, lnbActive, menu, setLnbActive]);

  const handleClickDropDown: MouseEventHandler = useCallback(() => {
    window.matchMedia('(hover: none), (pointer: coarse)').matches && setOpen(false);
  }, []);

  return (
    <>
      <Container
        active={navItemActive}
        isLastItem={isLastItem}
        onClickCapture={handleClick}
        {...referenceProps}
      >
        <NavLink item={menu}>
          {menu.title}
        </NavLink>
      </Container>
      {open && breakpoint === 'lg' && (menu.children ? (
        <GnbDropDownItemDiv
          {...floatingProps}
          data-testid={testIds.gnb.NavChildren}
          position={strategy}
          top={y ?? 0}
          left={x ?? 0}
          onClick={handleClickDropDown}
        >{menu.children.map((item) => (
            <NavLink
              key={item.title}
              item={item}
            >
              <GnbDropDownSpan>{item.title}</GnbDropDownSpan>
            </NavLink>
          ))}
        </GnbDropDownItemDiv>
      ) : (
        <HideDropDownDiv
          {...floatingProps}
          data-testid={testIds.gnb.NavChildren}
        />
      ))}
    </>
  );
};

export default NavItem;
